import { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../contexts/AuthContext';
import axios from 'axios';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Badge } from '../components/ui/badge';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from '../components/ui/dialog';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../components/ui/select';
import { toast } from 'sonner';
import { Plus, Pencil, Trash2, Search, Users } from 'lucide-react';

const clientCategories = ['fundador', 'normal', 'frecuente', 'eventos'];
const catLabels = { fundador: 'Fundador', normal: 'Normal', frecuente: 'Frecuente', eventos: 'Eventos' };
const catStyles = {
  fundador: 'bg-purple-100 text-purple-800',
  normal: 'bg-stone-100 text-stone-800',
  frecuente: 'bg-orange-100 text-orange-800',
  eventos: 'bg-blue-100 text-blue-800',
};

export default function ClientsPage() {
  const { API } = useAuth();
  const [clients, setClients] = useState([]);
  const [search, setSearch] = useState('');
  const [filterCat, setFilterCat] = useState('all');
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ name: '', phone: '', email: '', category: 'normal', notes: '' });

  const fetchClients = useCallback(() => {
    axios.get(`${API}/clients`).then(r => setClients(r.data)).catch(() => toast.error('Error al cargar clientes'));
  }, [API]);

  useEffect(() => { fetchClients(); }, [fetchClients]);

  const openCreate = () => { setEditing(null); setForm({ name: '', phone: '', email: '', category: 'normal', notes: '' }); setDialogOpen(true); };
  const openEdit = (c) => { setEditing(c); setForm({ name: c.name, phone: c.phone || '', email: c.email || '', category: c.category, notes: c.notes || '' }); setDialogOpen(true); };

  const handleSubmit = async () => {
    if (!form.name) { toast.error('El nombre es requerido'); return; }
    try {
      if (editing) {
        await axios.put(`${API}/clients/${editing.id}`, form);
        toast.success('Cliente actualizado');
      } else {
        await axios.post(`${API}/clients`, form);
        toast.success('Cliente creado');
      }
      setDialogOpen(false);
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Eliminar este cliente?')) return;
    try {
      await axios.delete(`${API}/clients/${id}`);
      toast.success('Cliente eliminado');
      fetchClients();
    } catch (e) { toast.error(e.response?.data?.detail || 'Error'); }
  };

  const filtered = clients.filter(c => {
    if (filterCat !== 'all' && c.category !== filterCat) return false;
    if (search && !c.name.toLowerCase().includes(search.toLowerCase()) && !(c.phone || '').includes(search)) return false;
    return true;
  });

  return (
    <div className="space-y-6 animate-fade-in" data-testid="clients-page">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div>
          <h1 className="font-outfit text-2xl sm:text-3xl font-semibold text-stone-900 tracking-tight">Clientes</h1>
          <p className="text-stone-500 text-sm mt-1">{clients.length} clientes registrados</p>
        </div>
        <Button data-testid="add-client-btn" onClick={openCreate} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full px-6 shadow-sm hover:shadow-md active:scale-[0.98] transition-all">
          <Plus className="w-4 h-4 mr-2" /> Nuevo Cliente
        </Button>
      </div>

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-stone-400" />
          <Input data-testid="client-search" value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar por nombre o telefono..." className="pl-10 rounded-xl border-stone-300" />
        </div>
        <Select value={filterCat} onValueChange={setFilterCat}>
          <SelectTrigger data-testid="client-filter-category" className="w-full sm:w-48 rounded-xl border-stone-300"><SelectValue placeholder="Categoria" /></SelectTrigger>
          <SelectContent>
            <SelectItem value="all">Todas</SelectItem>
            {clientCategories.map(c => <SelectItem key={c} value={c}>{catLabels[c]}</SelectItem>)}
          </SelectContent>
        </Select>
      </div>

      <Card className="bg-white border border-stone-200 shadow-sm rounded-2xl overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full" data-testid="clients-table">
            <thead>
              <tr className="bg-stone-50/50">
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Cliente</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Telefono</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Email</th>
                <th className="text-left p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Categoria</th>
                <th className="text-right p-4 text-xs font-semibold uppercase tracking-wider text-stone-500">Acciones</th>
              </tr>
            </thead>
            <tbody>
              {filtered.map(c => (
                <tr key={c.id} className="border-t border-stone-100 hover:bg-stone-50/50 transition-colors">
                  <td className="p-4">
                    <div className="flex items-center gap-3">
                      <div className="w-9 h-9 bg-stone-200 rounded-full flex items-center justify-center">
                        <span className="text-stone-600 text-xs font-semibold">{c.name.charAt(0).toUpperCase()}</span>
                      </div>
                      <span className="text-sm font-medium text-stone-900">{c.name}</span>
                    </div>
                  </td>
                  <td className="p-4 text-sm text-stone-600">{c.phone || '-'}</td>
                  <td className="p-4 text-sm text-stone-600">{c.email || '-'}</td>
                  <td className="p-4"><Badge className={`${catStyles[c.category]} border-0 rounded-full text-xs px-2.5 py-0.5`}>{catLabels[c.category]}</Badge></td>
                  <td className="p-4 text-right">
                    <div className="flex items-center justify-end gap-1">
                      <button data-testid={`edit-client-${c.id}`} onClick={() => openEdit(c)} className="p-2 rounded-lg hover:bg-stone-100 text-stone-500 hover:text-orange-700 transition-colors"><Pencil className="w-4 h-4" strokeWidth={1.5} /></button>
                      <button data-testid={`delete-client-${c.id}`} onClick={() => handleDelete(c.id)} className="p-2 rounded-lg hover:bg-red-50 text-stone-500 hover:text-red-700 transition-colors"><Trash2 className="w-4 h-4" strokeWidth={1.5} /></button>
                    </div>
                  </td>
                </tr>
              ))}
              {filtered.length === 0 && <tr><td colSpan={5} className="p-8 text-center text-stone-400 text-sm">No hay clientes</td></tr>}
            </tbody>
          </table>
        </div>
      </Card>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="rounded-2xl">
          <DialogHeader><DialogTitle className="font-outfit">{editing ? 'Editar Cliente' : 'Nuevo Cliente'}</DialogTitle></DialogHeader>
          <div className="space-y-4 py-2">
            <div><Label>Nombre</Label><Input data-testid="client-form-name" value={form.name} onChange={e => setForm({ ...form, name: e.target.value })} className="rounded-xl mt-1" /></div>
            <div className="grid grid-cols-2 gap-4">
              <div><Label>Telefono</Label><Input data-testid="client-form-phone" value={form.phone} onChange={e => setForm({ ...form, phone: e.target.value })} className="rounded-xl mt-1" /></div>
              <div><Label>Email</Label><Input data-testid="client-form-email" value={form.email} onChange={e => setForm({ ...form, email: e.target.value })} className="rounded-xl mt-1" /></div>
            </div>
            <div>
              <Label>Categoria</Label>
              <Select value={form.category} onValueChange={v => setForm({ ...form, category: v })}>
                <SelectTrigger data-testid="client-form-category" className="rounded-xl mt-1"><SelectValue /></SelectTrigger>
                <SelectContent>{clientCategories.map(c => <SelectItem key={c} value={c}>{catLabels[c]}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div><Label>Notas</Label><Input data-testid="client-form-notes" value={form.notes} onChange={e => setForm({ ...form, notes: e.target.value })} className="rounded-xl mt-1" /></div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setDialogOpen(false)} className="rounded-full">Cancelar</Button>
            <Button data-testid="client-form-submit" onClick={handleSubmit} className="bg-orange-700 hover:bg-orange-800 text-white rounded-full">Guardar</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
